import { css } from "@linaria/core";
import { styled } from "@linaria/react";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { Outlet, useOutletContext } from "react-router";
import type { Project } from "../../../../api/useProjects";
import t from "../../../../i18n";
import { toPath } from "../../../../router/helpers/path";
import { color, fonts } from "@ui";
import Secondary from "@ui/Header/Secondary";
import SecondaryHeader from "@ui/Header/SecondaryHeader";
import {
  BANNER_HEIGHT_CSS_VARIABLE,
  PRIMARY_HEADER_HEIGHT_CSS_VARIABLE,
  SECONDARY_HEADER_HEIGHT_CSS_VARIABLE,
} from "@ui/Header/useHeaderHeight";
import Link from "@ui/Link";
import LoadingBoundary from "@ui/LoadingBoundary";
import SidePanelLayout from "@ui/SidePanel/Layout";
import { colors } from "@ui/Theme";
import Navigation from "./Navigation";
import CreateFormulaQuantitySidePanel from "./SidePanel/CreateFormulaQuantitySidePanel";
import EditQuantitySidePanel from "./SidePanel/EditQuantitySidePanel";
import type { EditableQuantity } from "./view/Quantities/useMetadata";
import { useSidePanelOpened } from "../../useSidePanel";

const BreadcrumbWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
`;

const StyledLink = styled(Link)`
  color: ${color.lightNitrogen500_Main};
  font: ${fonts.body1};
  font-weight: 500;
  :hover:not(.disabled) {
    text-decoration: underline;
  }
  &.disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const secondaryCss = css`
  background-color: ${colors.titanium50};
  padding: 0;

  margin-bottom: 8px;
  z-index: 5;

  position: sticky;
  top: calc(
    var(${PRIMARY_HEADER_HEIGHT_CSS_VARIABLE}, 0px) +
      var(${SECONDARY_HEADER_HEIGHT_CSS_VARIABLE}, 0px) + var(${BANNER_HEIGHT_CSS_VARIABLE}, 0px)
  );
`;

const PanelCss = css`
  --secondary-header-height: 35px;
  --thead-sticky-offset: 41px;
  gap: 0;
`;

interface P {
  project: Project;
}

const Breadcrumb = ({ project }: P) => (
  <BreadcrumbWrapper>
    <StyledLink to={toPath("/projects/:projectId/settings", { projectId: project.id })}>
      {t("project.secondary_header.settings.title")}
    </StyledLink>
    <div>{">"}</div>
    <div>{t("project.secondary_header.quantities.title")}</div>
  </BreadcrumbWrapper>
);

export type QuantityLayoutOutletContext = {
  project: Project;
  isQuantityPanelClosed: boolean;
  onEditQuantity?: (q: EditableQuantity, focusFormula?: boolean) => void;
  onOpenCreateQuantityPanel: () => void; // Added new prop
};

export function QuantitiesLayout() {
  const project = useOutletContext<Project>();
  if (!project) {
    throw new Error("no project");
  }

  const [activeProjectSidePanel, setActiveProjectSidePanel] = useSidePanelOpened({
    scope: project.id,
  });
  const [activeQuantitySidePanel, setActiveQuantitySidePanel] = useState<
    "none" | "create" | "edit"
  >("none");
  const [selectedQuantity, setSelectedQuantity] = useState<EditableQuantity>();
  const [sidePanelFocusFormulaeView, setSidePanelFocusFormulaeView] = useState<boolean>(false);

  useEffect(() => {
    if (activeProjectSidePanel) {
      setActiveQuantitySidePanel("none");
    }
  }, [activeProjectSidePanel]);

  const handleOpenCreatePanel = React.useCallback(() => {
    setSelectedQuantity(undefined);
    setActiveQuantitySidePanel("create");
    setActiveProjectSidePanel(false);
  }, [setActiveProjectSidePanel]);

  const handleEditQuantity = React.useCallback(
    (quantity: EditableQuantity, focusFormula = false) => {
      setSelectedQuantity(quantity);
      setActiveQuantitySidePanel("edit");
      setSidePanelFocusFormulaeView(focusFormula);
      setActiveProjectSidePanel(false);
    },
    [setActiveProjectSidePanel],
  );

  const handleClosePanel = React.useCallback(() => {
    setActiveQuantitySidePanel("none");
    setSelectedQuantity(undefined);
    setActiveProjectSidePanel(false);
  }, [setActiveProjectSidePanel]);

  const isPanelFolded = activeQuantitySidePanel === "none";

  return (
    <div
      className={classNames(quantities, {
        isFolded: isPanelFolded,
      })}
    >
      <div className={quantitiesContent}>
        <SecondaryHeader breadcrumb={<Breadcrumb project={project} />} />
        <SidePanelLayout className={PanelCss}>
          <Secondary className={secondaryCss} nav={<Navigation projectId={project.id} />} />
          <LoadingBoundary>
            <Outlet
              context={
                {
                  project,
                  isQuantityPanelClosed: isPanelFolded,
                  onEditQuantity: handleEditQuantity,
                  onOpenCreateQuantityPanel: handleOpenCreatePanel,
                } satisfies QuantityLayoutOutletContext
              }
            />
          </LoadingBoundary>
        </SidePanelLayout>
      </div>

      {activeQuantitySidePanel === "create" && (
        <CreateFormulaQuantitySidePanel
          projectId={project.id}
          timezone={project.timezone}
          isFolded={false}
          closePanel={handleClosePanel}
        />
      )}

      {activeQuantitySidePanel === "edit" && selectedQuantity && (
        <LoadingBoundary>
          <EditQuantitySidePanel
            projectId={project.id}
            timezone={project.timezone}
            isFolded={false}
            closePanel={handleClosePanel}
            quantity={selectedQuantity}
            focusFormulaeView={sidePanelFocusFormulaeView}
          />
        </LoadingBoundary>
      )}
    </div>
  );
}

//#region CSS
// eslint-disable-next-line @typescript-eslint/no-unused-expressions
css`
  :global() {
    :root {
      --side-bar-width: 520px;
      --section-column-transition-duration: 350ms;
      --section-column-transition-ease: cubic-bezier(0.4, 0, 0.2, 1);
    }
  }
`;

const quantities = css`
  --right-column-width: var(--side-bar-width);
  position: relative;
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) var(--right-column-width);
  grid-auto-rows: auto;
  flex-grow: 1;
  transition: grid-template-columns var(--section-column-transition-duration)
    var(--section-column-transition-ease);
  color: ${colors.carbon900};
  &.isFolded {
    --right-column-width: 0;
  }
`;

const quantitiesContent = css`
  display: flex;
  flex-direction: column;
  height: fit-content;
`;

//#endregion CSS
